import { useActivityPageContext } from "PFApp/activities/show/activity_page_context";
import { useNotesPermissions } from "PFApp/activities/show/notes/use_notes_permissions";
import AddToComparisonButton from "PFApp/activities/show/parts/add_to_comparison_button.jsx";
import ChatWithIcon from "PFApp/activities/show/parts/chat_with_icon";
import displayShortlistReasons from "PFApp/activities/show/parts/display_shortlist_reasons";
import { useActivityChat } from "PFApp/activities/show/parts/hooks/use_activity_chat";
import AvailabilityWarningIcon from "PFApp/activities/show/shortlist/availability_warning_icon";
import ShortlistCreator from "PFApp/activities/show/shortlist/shortlist_creator";
import ShortlistItemButtons from "PFApp/activities/show/shortlist/shortlist_item_buttons";
import { ShortlistItemNotes } from "PFApp/activities/show/shortlist/shortlist_item_notes/shortlist_item_notes";
import ProfileCardConnected from "PFApp/components/profile_card_connected";
import Pill from "PFComponents/pill/pill";
import { scoresShapePropType } from "PFCore/base/prop_types/common_prop_types";
import { isProfileActive } from "PFCore/helpers/profile";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useShortlistDelete } from "PFCore/hooks/queries/shortlists/use_shortlist_delete";
import CloseIcon from "PFIcons/close.svg";
import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";

import css from "./shortlist_item.less";

const ShortlistItem = ({ shortlistedProfile, withChat }) => {
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation("activities");
  const { startChat } = useActivityChat();

  const {
    task,
    isProfileInComparison,
    addProfileToComparison,
    removeProfileFromComparison,
    isActivityUnbiased
  } = useActivityPageContext();

  const { mutate: deleteShortlist } = useShortlistDelete({ activityId: task.id });

  const { shouldDisplayNotes } = useNotesPermissions({ activity: task });

  const isInterested =
    task.watchers && task.watchers.find(({ profile: { id } }) => id === shortlistedProfile.profile.id);
  const isReviewer = task.reviewers?.find(({ profile_id }) => profile_id === currentProfile.id);

  const isDraft = task.state === "draft";
  const reasonState =
    shortlistedProfile.state === "declined" ? "declined" : task.coowned ? "accepted" : "approved";
  const showCloseButton = shortlistedProfile.state === "pending" && !isReviewer;

  // do not render availability if profile is filled
  if (shortlistedProfile.scores && shortlistedProfile.state === "filled") {
    shortlistedProfile.scores.availability_score = null;
  }

  const isActive = isProfileActive(shortlistedProfile.profile);
  const isMe = shortlistedProfile.profile.id === currentProfile.id;

  return (
    <ProfileCardConnected
      profileResponse={shortlistedProfile}
      gaugeProps={{
        availabilityIcon:
          shortlistedProfile?.scores?.availability_score <= 0 ? <AvailabilityWarningIcon /> : null
      }}
      embeded
      isColumn
      isBlindModeOn={isActivityUnbiased}
      Icon={!isMe && withChat && !isDraft && isActive && ChatWithIcon}
      handleIconClick={() => startChat(shortlistedProfile.profile)}
      currentProfile={currentProfile}
      actions={
        <div>
          {shortlistedProfile.creator && <ShortlistCreator item={shortlistedProfile} />}
          {shouldDisplayNotes && <ShortlistItemNotes />}
          <ShortlistItemButtons shortlistedProfile={shortlistedProfile} isBlindModeOn={isActivityUnbiased} />
          {isInterested && (
            <small className={css.interested}>{t("show.shortlist.personInterestedInActivity")}</small>
          )}
        </div>
      }
      topRightCornerNode={
        // emptyContent div to keep alingment on profile card
        shortlistedProfile.external && (
          <div className={css.topRight}>
            <div className={css.emptyContent} />
            <Pill style={{ marginLeft: 5 }}>{t("show.shortlist.addedFromExternal")}</Pill>
          </div>
        )
      }
      headerActions={
        <div className={css.headerActions}>
          {isActive && (
            <AddToComparisonButton
              isItemInComparision={isProfileInComparison(shortlistedProfile.profile.id)}
              removeItemFromComparison={() => removeProfileFromComparison(shortlistedProfile.profile.id)}
              addItemToComparison={() => addProfileToComparison(shortlistedProfile.profile.id)}
            />
          )}
          {showCloseButton && (
            <button
              className={css.removeShortlistedItem}
              onClick={() => {
                deleteShortlist(shortlistedProfile.id);
              }}
              title={t("show.shortlist.removeShortlistItem")}
            >
              <CloseIcon />
            </button>
          )}
        </div>
      }
      footer={
        shortlistedProfile.reasons &&
        (shortlistedProfile.reasons.other ||
          (shortlistedProfile.reasons.list && shortlistedProfile.reasons.list.length > 0)) && (
          <div className={css.footer}>
            <Pill style={{ marginTop: 10, marginBottom: 10 }}>
              {t("show.shortlist.candidateBasedOn", {
                reasonState: reasonState,
                shortlistReasons: displayShortlistReasons(shortlistedProfile.reasons)
              })}
            </Pill>
          </div>
        )
      }
    />
  );
};

ShortlistItem.propTypes = {
  shortlistedProfile: PropTypes.shape({
    id: PropTypes.number,
    profile: PropTypes.object,
    creator: PropTypes.object,
    external: PropTypes.bool,
    reasons: PropTypes.shape({
      list: PropTypes.arrayOf(PropTypes.string),
      other: PropTypes.string
    }),
    state: PropTypes.string,
    scores: scoresShapePropType
  }).isRequired,
  withChat: PropTypes.bool.isRequired,
  otherConversations: PropTypes.array,
  participantsOfConversations: PropTypes.object,
  actionsComponent: PropTypes.func
};

export default ShortlistItem;
